/**
 * ============================================================================
 * useRetireMutation — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: POST /api/v1/equipment/{id}/retire — high-blast-radius action
 *          requiring two distinct signers + a ≥200-char rationale. The server
 *          enforces both. Mirrors the supplier-disqualify dual-sig pattern.
 * ============================================================================
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from '../../api/client';
import type { EquipmentDetail } from './useEquipmentDetailQuery';

export interface RetireInput {
  equipmentId: string;
  rationale: string;
  signature_bundle_id_a: string;
  signature_bundle_id_b: string;
  /** When true the server proceeds straight to DECOMMISSIONED. */
  decommission?: boolean;
}

export function useRetireMutation(): UseMutationResult<
  EquipmentDetail,
  Error,
  RetireInput
> {
  return useMutation<EquipmentDetail, Error, RetireInput>({
    mutationFn: ({ equipmentId, ...body }) =>
      apiFetch<EquipmentDetail>(
        `/api/v1/equipment/${encodeURIComponent(equipmentId)}/retire`,
        {
          method: 'POST',
          body,
          idempotencyKey: newIdempotencyKey(),
        },
      ),
  });
}
